import React, { useMemo } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import TextInput from '../../../components/FormElements/TextInput'
import { Button } from '@demandlocal/components'
import PrimaryButton from '../../../components/Buttons/PrimaryButton'
import { useModal } from '../../../components/useModal'
import { useVerifyFTPUploadFile } from '../../../api/dealership/customers/util/useVerifyFTPUploadFile'
import SelectInput from '../../../components/FormElements/SelectInput'
import moment from 'moment'
import FTPFileMapper from './FTPFileMapper'
import { timezonesOptions } from '../../../lib/utils/timezones'
import { UseMutationResult } from 'react-query'
import toast from 'react-hot-toast'

type Props = {
  ftpUploadCSVMutation: UseMutationResult<any>
  databaseColumns?: Array<string>
}

const CustomersFTPUpload = (props: Props): JSX.Element => {
  const { ftpUploadCSVMutation, databaseColumns } = props
  const { closeModal, handleModal } = useModal()
  const formContext = useForm({
    mode: 'onSubmit',
  })
  const { control, handleSubmit, watch, formState } = formContext

  const formValues = watch()
  const { mutateAsync: verifyFTPUploadFile } = useVerifyFTPUploadFile()

  const onSubmit = async (values): Promise<void> => {
    const toastId = toast.loading('Verifying file...')
    try {
      await verifyFTPUploadFile(
        {
          data: {
            host: values.ftp_url,
            user: values.ftp_username,
            password: values.ftp_password,
          },
        },
        {
          onSuccess: (data) => {
            toast.dismiss(toastId)
            handleModal(
              <FTPFileMapper
                data={data}
                ftpConnection={values}
                ftpUploadCSVMutation={ftpUploadCSVMutation}
                databaseColumns={databaseColumns}
              />,
            )
          },
        },
      )
    } catch (e) {
      toast.error(
        'There was an error verifying your file. Please check your credentials and/or file and try again.',
        {
          id: toastId,
        },
      )
    }
  }

  const hoursOptions = useMemo(
    () =>
      Array(48)
        // @ts-ignore
        .fill()
        .map((_, index) =>
          moment({
            hour: Math.floor(0.5 * index),
            minute: 30 * (index % 2),
          }).format('h:mm A'),
        )
        .map((hour) => ({
          label: hour,
          value: hour,
        })),
    [],
  )

  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col px-12 overflow-auto'}>
          <div className={'font-bold text-blue-primary text-[28px]'}>Import from FTP</div>
          <div className={'pt-8 pb-8'}>
            <div className={'w-1/2'}>
              <TextInput
                rules={{ required: 'This field is required' }}
                inverted
                placeholder={'Ex: ftp://mydomain.com/products.csv'}
                name={'ftp_url'}
                label={'File URL'}
                control={control}
                info={
                  'A fully qualified URL to access your file. For example: ftp://mydomain.com/products.csv'
                }
              />
            </div>
          </div>
          <div className={'pb-8 border-b-2'}>
            <div className={'font-bold text-blue-primary text-sm'}>
              If your URL is pasword protected, please provide your login info
            </div>
            <div className={'w-1/2 pt-7'}>
              <TextInput
                inverted
                placeholder={'FTP Username (Optional)'}
                name={'ftp_username'}
                label={'Username'}
                control={control}
              />
            </div>
            <div className={'w-1/2 pt-7'}>
              <TextInput
                inverted
                placeholder={'FTP Password (Optional)'}
                name={'ftp_password'}
                label={'Password'}
                control={control}
              />
            </div>
          </div>
          <div className={'mt-7'}>
            <div className={'flex gap-5 mb-7'}>
              <div className={'w-full max-w-[50%]'}>
                <SelectInput
                  required={'This field is required'}
                  name={'fetch_frequency'}
                  label={'Fetch frequency'}
                  control={control}
                  options={[
                    { label: 'One time', value: 'one_time' },
                    { label: 'Daily', value: 'daily' },
                    { label: 'Weekly', value: 'weekly' },
                  ]}
                />
              </div>
              {formValues.fetch_frequency?.value === 'weekly' && (
                <SelectInput
                  required={'This field is required'}
                  name={'fetch_day'}
                  label={'Fetch time'}
                  control={control}
                  options={[
                    { label: 'Monday', value: 'monday' },
                    { label: 'Tuesday', value: 'tuesday' },
                    { label: 'Wednesday', value: 'wednesday' },
                    { label: 'Thursday', value: 'thursday' },
                    { label: 'Friday', value: 'friday' },
                    { label: 'Saturday', value: 'saturday' },
                    { label: 'Sunday', value: 'sunday' },
                  ]}
                />
              )}
              {formValues.fetch_frequency &&
                formValues.fetch_frequency?.value !== 'one_time' && (
                  <SelectInput
                    required={'This field is required'}
                    disabled={
                      formValues.fetch_frequency?.value === 'one_time' ||
                      !formValues.fetch_frequency
                    }
                    name={'fetch_time'}
                    label={'Fetch time'}
                    control={control}
                    options={hoursOptions}
                  />
                )}
            </div>
            {formValues.fetch_frequency &&
              formValues.fetch_frequency?.value !== 'one_time' && (
                <SelectInput
                  required={'This field is required'}
                  disabled={
                    formValues.fetch_frequency?.value === 'one_time' ||
                    !formValues.fetch_frequency
                  }
                  name={'timezone'}
                  label={'Timezone'}
                  control={control}
                  options={timezonesOptions}
                />
              )}
          </div>
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4 px-12'}>
          <Button
            variant={'secondary'}
            disabled={formState.isSubmitting}
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button disabled={formState.isSubmitting} type={'submit'}>
            Import
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default CustomersFTPUpload
